/** PWA: SW + instalar app + Web Push (avisos de ticket). */
const INSTALLED_KEY = 'pwa-installed'

let deferredPrompt = null
let swReady = false
/** Cache sync del estado «ya instalada» (standalone / flag / related apps). */
let installedCache = null

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches
    || window.navigator.standalone === true
}

/** true si la PWA ya está instalada o abierta como app — no ofrecer instalar. */
export function isPwaInstalled() {
  if (installedCache === true) return true
  if (isStandalone()) {
    rememberInstalled()
    return true
  }
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return true
  } catch (_) {}
  return false
}

function rememberInstalled() {
  installedCache = true
  try {
    localStorage.setItem(INSTALLED_KEY, '1')
  } catch (_) {}
}

async function detectInstalledRelatedApps() {
  if (!navigator.getInstalledRelatedApps) return false
  try {
    const apps = await navigator.getInstalledRelatedApps()
    return Array.isArray(apps) && apps.length > 0
  } catch (_) {
    return false
  }
}

export function registerPwa() {
  if (isPwaInstalled()) {
    hideAllInstallUi()
  }

  if (!('serviceWorker' in navigator)) return

  const register = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        swReady = true
        try { reg.update() } catch (_) {}
        refreshInstallButtons()
      })
      .catch(() => {
        swReady = false
      })
  }

  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register)

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    if (isPwaInstalled()) {
      deferredPrompt = null
      hideAllInstallUi()
      return
    }
    deferredPrompt = e
    // Solo un CTA: el botón del nav. Sin banner flotante redundante.
    refreshInstallButtons()
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    rememberInstalled()
    hideAllInstallUi()
  })

  detectInstalledRelatedApps().then((related) => {
    if (related) {
      rememberInstalled()
      hideAllInstallUi()
    } else {
      refreshInstallButtons()
    }
  })

  // iOS: solo pista si aún no está en pantalla de inicio
  if (isIos() && !isPwaInstalled() && !sessionStorage.getItem('pwa-ios-hint-dismissed')) {
    showIosHint()
  }

  navigator.serviceWorker?.addEventListener('message', (event) => {
    const data = event.data || {}
    if (data.type === 'soporte-navigate' && typeof data.url === 'string') {
      try {
        const path = new URL(data.url, window.location.origin).pathname + new URL(data.url, window.location.origin).search
        if (path && path !== window.location.pathname + window.location.search) {
          window.location.assign(data.url)
        }
      } catch (_) {}
    }
  })
}

/** Enlaza botones [data-pwa-install] tras cada navegación SPA. */
export function wireInstallButtons(root = document) {
  root.querySelectorAll('[data-pwa-install]').forEach((btn) => {
    if (btn.dataset.pwaBound === '1') return
    btn.dataset.pwaBound = '1'
    btn.addEventListener('click', (ev) => {
      ev.preventDefault()
      installApp()
    })
  })
  refreshInstallButtons(root)
}

export async function installApp() {
  if (isPwaInstalled()) {
    hideAllInstallUi()
    return
  }

  if (deferredPrompt) {
    deferredPrompt.prompt()
    try {
      const choice = await deferredPrompt.userChoice
      if (choice?.outcome === 'accepted') {
        rememberInstalled()
      }
    } catch (_) {}
    deferredPrompt = null
    hideInstallBanner()
    refreshInstallButtons()
    return
  }

  if (isIos()) {
    showHelpModal(
      'Instalar en iPhone / iPad',
      '1. Abre esta página en Safari\n2. Pulsa el botón Compartir\n3. Elige «Añadir a pantalla de inicio»\n4. Confirma «Añadir»'
    )
    return
  }

  if (isAndroid()) {
    showHelpModal(
      'Instalar en Android',
      '1. Menú del navegador (⋮)\n2. «Instalar aplicación» o «Añadir a pantalla de inicio»\n3. Confirma la instalación\n\nSi no aparece la opción, recarga la página y vuelve a intentarlo en unos segundos.'
    )
    return
  }

  showHelpModal(
    'Instalar en el ordenador',
    'En Chrome o Edge: mira el icono de instalar (⊕ / monitor) a la derecha de la barra de direcciones y pulsa «Instalar».\n\nTambién: menú ⋮ → «Instalar Soporte AGH» / «Aplicaciones» → Instalar.'
  )
}

/**
 * Activa Web Push para un ticket (permiso + suscripción VAPID → API portal).
 * @param {string} token
 * @param {string} vapidPublicKey
 * @param {(path: string, body: object) => Promise<any>} subscribeApi
 */
export async function enableTicketPush(token, vapidPublicKey, subscribeApi) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    throw new Error('Este dispositivo no admite notificaciones push.')
  }
  if (!vapidPublicKey) {
    throw new Error('Push no configurado en el servidor.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones denegado.')
  }

  const reg = await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  }

  const json = sub.toJSON()
  await subscribeApi(token, {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
    contentEncoding: (PushManager.supportedContentEncodings && PushManager.supportedContentEncodings[0]) || 'aes128gcm',
  })

  return sub
}

export async function disableTicketPush(token, unsubscribeApi) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  try {
    await sub.unsubscribe()
  } catch (_) {}
  if (endpoint) {
    await unsubscribeApi(token, endpoint)
  }
}

export async function currentPushEndpoint() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    return sub?.endpoint || null
  } catch (_) {
    return null
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

function refreshInstallButtons(root = document) {
  const hide = isPwaInstalled()
  if (hide) {
    hideAllInstallUi()
    return
  }
  root.querySelectorAll('[data-pwa-install]').forEach((btn) => {
    btn.hidden = false
    btn.textContent = 'Instalar app'
  })
}

function hideAllInstallUi() {
  document.querySelectorAll('[data-pwa-install]').forEach((btn) => {
    btn.hidden = true
  })
  hideInstallBanner()
  document.getElementById('pwa-ios-hint')?.remove()
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isAndroid() {
  return /android/i.test(navigator.userAgent)
}

function showInstallBanner() {
  // Banner flotante desactivado: un solo CTA en la barra de navegación.
}

function hideInstallBanner() {
  document.getElementById('pwa-install-banner')?.remove()
}

function showIosHint() {
  if (isPwaInstalled() || document.getElementById('pwa-ios-hint')) return
  const el = document.createElement('div')
  el.id = 'pwa-ios-hint'
  el.className = 'pwa-install-banner pwa-ios-hint'
  el.innerHTML = `
    <div class="pwa-install-text">
      <strong>Añadir a inicio</strong>
      <span>Safari → Compartir → «Añadir a pantalla de inicio»</span>
    </div>
    <button type="button" class="pwa-install-dismiss" id="pwa-ios-no" aria-label="Cerrar">×</button>
  `
  document.body.appendChild(el)
  el.querySelector('#pwa-ios-no')?.addEventListener('click', () => {
    sessionStorage.setItem('pwa-ios-hint-dismissed', '1')
    el.remove()
  })
}

function showHelpModal(title, body) {
  document.getElementById('pwa-help-modal')?.remove()
  const el = document.createElement('div')
  el.id = 'pwa-help-modal'
  el.className = 'pwa-help-modal'
  el.innerHTML = `
    <div class="pwa-help-dialog" role="dialog" aria-modal="true" aria-labelledby="pwa-help-title">
      <h2 id="pwa-help-title">${escapeHtml(title)}</h2>
      <p class="pwa-help-body">${escapeHtml(body).replace(/\n/g, '<br>')}</p>
      <button type="button" class="portal-btn portal-btn-primary" id="pwa-help-ok">Entendido</button>
    </div>
  `
  document.body.appendChild(el)
  const close = () => el.remove()
  el.querySelector('#pwa-help-ok')?.addEventListener('click', close)
  el.addEventListener('click', (e) => { if (e.target === el) close() })
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

void swReady
void showInstallBanner
